import { ref, onMounted, onUnmounted, getCurrentInstance, type Ref } from "vue";
import { getBuildVersion } from "@/helpers/buildVersion";

// To test locally:
// 1. Add VITE_VERSION_CHECK_ENABLED=true to .env.local and restart the dev server.
// 2. Load the app in the browser — do NOT refresh again after this point.
// 3. Edit public/version.json and change the version string to anything different.
// 4. Wait up to VITE_VERSION_CHECK_INTERVAL_MS (default 60s) or tab away and back — the dialog should appear.
// Note: refreshing reloads the bundle, which re-reads the baked-in version, so the mismatch disappears.
const POLL_INTERVAL_MS = Number(import.meta.env.VITE_VERSION_CHECK_INTERVAL_MS) || 60 * 1000;
export const SNOOZE_MS = Number(import.meta.env.VITE_VERSION_CHECK_SNOOZE_MS) || 5 * 60 * 1000;

// Module-level shared state — all callers share the same reactive ref
const updateAvailable = ref(false);
let snoozedUntil: number | null = null;

interface UseVersionCheckOptions {
	devMode?: boolean;
}

interface UseVersionCheckReturn {
	updateAvailable: Ref<boolean>;
	start: () => void;
	stop: () => void;
	snooze: (ms: number) => void;
}

export function formatSnoozeDuration(ms: number): string {
	const seconds = Math.round(ms / 1000);
	if (seconds < 60) return `${seconds} sec`;
	const minutes = Math.round(seconds / 60);
	if (minutes < 60) return `${minutes} min`;
	return `${Math.round(minutes / 60)} hr`;
}

export { getBuildVersion } from "@/helpers/buildVersion";

export function snooze(ms: number): void {
	snoozedUntil = Date.now() + ms;
	updateAvailable.value = false;
}

/** Reset module-level state — for use in tests only. */
export function _resetVersionCheck(): void {
	updateAvailable.value = false;
	snoozedUntil = null;
}

export function useVersionCheck(options: UseVersionCheckOptions = {}): UseVersionCheckReturn {
	// DEV is true in both "development" mode and "test" mode, so check MODE explicitly.
	// Set VITE_VERSION_CHECK_ENABLED=true in .env.local to force polling on in dev.
	const isDev = options.devMode ?? (import.meta.env.MODE === "development" && !import.meta.env.VITE_VERSION_CHECK_ENABLED);

	const noop = () => {};
	if (isDev) {
		return { updateAvailable, start: noop, stop: noop, snooze: noop };
	}

	const currentVersion = getBuildVersion();
	console.info(`[version-check] Running version: ${currentVersion}`);

	let intervalId: ReturnType<typeof setInterval> | null = null;

	const checkVersion = async (): Promise<void> => {
		if (updateAvailable.value) return;
		if (snoozedUntil !== null && Date.now() < snoozedUntil) return;
		try {
			const response = await fetch("/version.json", { cache: "no-cache" });
			if (!response.ok) return;
			const data = await response.json();
			if (data?.version && data.version !== currentVersion) {
				console.info(`[version-check] New version available: ${data.version} (running: ${currentVersion})`);
				updateAvailable.value = true;
			}
		} catch {
			// Silently ignore network errors or malformed JSON — never block the user
		}
	};

	const onVisibilityChange = (): void => {
		if (document.visibilityState === "visible") {
			checkVersion();
		}
	};

	const start = (): void => {
		checkVersion();
		intervalId = setInterval(checkVersion, POLL_INTERVAL_MS);
		document.addEventListener("visibilitychange", onVisibilityChange);
	};

	const stop = (): void => {
		if (intervalId !== null) {
			clearInterval(intervalId);
			intervalId = null;
		}
		document.removeEventListener("visibilitychange", onVisibilityChange);
	};

	// Auto-register lifecycle hooks when called inside a component context (e.g. App.vue setup())
	if (getCurrentInstance()) {
		onMounted(start);
		onUnmounted(stop);
	}

	return { updateAvailable, start, stop, snooze };
}
